import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { TranslationService } from '@sdbn/common';
import { FilterAdministrationContext } from '../../filter-administration.context';
import { ConditionType, FilterCondition, EditorFilterTreeNode, LogicalOperator } from '../../filter-administration.model';
import { FilterEditorRegistryService } from '../../filter-editor-registry.service';
import { FilterService } from '../../filter.service';

const source = 'app-node-editor';

@Component({
  selector: 'app-node-editor',
  templateUrl: './node-editor.component.html',
  styleUrls: ['./node-editor.component.scss']
})
export class NodeEditorComponent implements OnInit, OnDestroy {

  private draggedConditionType: ConditionType;
  conditionDropzoneActive = false;

  private draggedFilterCondition: FilterCondition;

  filterAdministrationContext: FilterAdministrationContext;
  filterAdministrationContextSubscription: Subscription;

  sortedConditions = new Array<FilterCondition>();

  node: EditorFilterTreeNode;

  @Input() set setNode(node: EditorFilterTreeNode) {
    this.node = node;
    this.updateSortedConditions();
  }

  @Input() set setfilterAdministrationContext(filterAdministrationContext: FilterAdministrationContext) {
    this.filterAdministrationContext = filterAdministrationContext;
    this.filterAdministrationContextSubscription = this.filterAdministrationContext.contextChange$.subscribe(
      change => {
        try {
          if (change.key === 'dragged-condition-type') {
            this.draggedConditionType = change.value;
            this.conditionDropzoneActive = this.draggedConditionType != null;
          }

          if (change.key === 'dragged-filter-condition') {
            this.draggedFilterCondition = change.value;
            this.conditionDropzoneActive = this.draggedFilterCondition != null &&
              this.node.conditions.indexOf(this.draggedFilterCondition) === -1;
          }
        } catch (error) {
          console.error(error);
        }
      }
    );
  }

  constructor(
    public translationService: TranslationService,
    private dialog: MatDialog,
    private filterService: FilterService,
    private filterEditorRegistryService: FilterEditorRegistryService
  ) { }

  ngOnInit(): void {
  }

  ngOnDestroy() {
    if (this.filterAdministrationContextSubscription != null) {
      this.filterAdministrationContextSubscription.unsubscribe();
    }
  }

  public onDragOverConditionsList(event: DragEvent) {
    if (this.draggedConditionType != null) {
      event.preventDefault();
      return;
    }
    if (this.conditionDropzoneActive) {
      event.preventDefault();
      return;
    }
  }

  public onDropOptionToConditionsList(event: DragEvent) {
    if (this.draggedConditionType != null) {
      const newCondition = new FilterCondition({
        id: this.filterAdministrationContext.getNextTransientId(),
        type: this.draggedConditionType.type,
        data: this.draggedConditionType.defaultData != null ? JSON.parse(JSON.stringify(this.draggedConditionType.defaultData)) : {}
      });

      this.node.conditions.push(newCondition);

      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", "");
      this.filterAdministrationContext.setDraggedConditionTypeAdd(null, source);

      this.filterAdministrationContext.getFilter().dirty = true;
      this.updateSortedConditions();

      this.filterAdministrationContext.setSelectedFilterCondition(newCondition, source)
      event.preventDefault();
    }

    // Verschieben innerhalb der struktur
    if (this.conditionDropzoneActive && this.draggedFilterCondition != null) {
      // Löschen der Condition in Ursprungsknoten
      this.filterService.removeConditionFromFilter(this.filterAdministrationContext.getFilter(), this.draggedFilterCondition);

      // Neu Erzeugen der condition
      this.node.conditions.push(new FilterCondition({
        id: this.filterAdministrationContext.getNextTransientId(),
        data: this.draggedFilterCondition.data,
        type: this.draggedFilterCondition.type
      }));

      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", "");
      this.filterAdministrationContext.setDraggedFilterCondition(null, source);

      this.filterAdministrationContext.getFilter().dirty = true;
      this.updateSortedConditions();

      event.preventDefault();
    }
  }

  public addChildNode() {
    this.node.nodes.push(new EditorFilterTreeNode(
      {
        filterId: this.node.filterId,
        id: this.filterAdministrationContext.getNextTransientId(),
        operator: this.node.operator === LogicalOperator.OR ? LogicalOperator.AND : LogicalOperator.OR,
        conditions: new Array<FilterCondition>(),
        parentNode: this.node,
        nodes: new Array<EditorFilterTreeNode>()
      })
    );
    this.filterAdministrationContext.getFilter().dirty = true;
  }

  public deleteNode() {
    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.widgets.filter-administration.note-editor.delete.confirm-dialog.title',
      messageKey: 'tmc-yard.widgets.filter-administration.note-editor.delete.confirm-dialog.message',
      confirm: () => {
        this.node.deleted = true;
        if (this.node.id <= 0) {
          const index = this.node.parentNode.nodes.indexOf(this.node);
          this.node.parentNode.nodes.splice(index, 1);
          this.filterAdministrationContext.getFilter().dirty = true;
        }
      }
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public selectNode() {
    this.filterAdministrationContext.setSelectedFilterTreeNoe(this.node, source);
  }

  private updateSortedConditions() {

    this.sortedConditions = new Array<FilterCondition>();

    this.sortedConditions.push(...this.node.conditions);
    this.sortedConditions.sort((a, b) => {

      const dictA = this.filterEditorRegistryService.getConditionType(a.type)?.nameDictionaryKey;
      const dictB = this.filterEditorRegistryService.getConditionType(b.type)?.nameDictionaryKey;

      let ta = this.translationService.translate(dictA);
      if (ta == null) {
        ta = 'undefined';
      }
      ta = ta.toLowerCase().replace(' ', '');

      let tb = this.translationService.translate(dictB);
      if (tb == null) {
        tb = 'undefined';
      }
      tb = tb.toLowerCase();
      tb = tb.toLowerCase().replace(' ', '');

      return ta > tb ? 1 : ta === tb ? 0 : -1;
    });
  }
}
